import { memo, useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import styles from "./LoanRequestPage.module.scss";
import GridSearchBar from "../../../common/components/grid-search-bar/GridSearchBar";
import TabSelector from "../../../common/components/tab-selector/TabSelector";
import LoanRequestDetails from "./components/LoanRequestDetails/LoanRequestDetails";
import CommonPagination from "../../../common/components/pagination/CommonPagination";
import {useGetLoanRequestQuery } from "../../../service/order-service";
import FilterBy from "../../../common/components/filter-by/FilterBy";
import { Order } from "../../../model/loan-request";
import moment from "moment";
import { useConfigLayout } from "../../../hooks/useConfigLayout";

type Props = {};

const LoanRequestPage = memo((props: Props) => {
	const navigate = useNavigate();
	const { selectedCountry } = useConfigLayout();

	const [searchValue, setSearchValue] = useState<string>("");
	const [debounceSearchValue, setDebounceSearchValue] = useState<string>("");
	const [selectedOrderType, setSelectedOrderType] = useState<"Installment"| "Pay As You Go">("Installment");

	const pageSize = 10;
	const [currentPage, setCurrentPage] = useState(1);
	const [totalPages, setTotalPages] = useState(0);
	const [loading, setLoading] = useState(false);

	const [selectedTab, setSelectedTab] = useState(1);
	const [data, setData] = useState<Order[]>();
	const [orderDetails, setOrderDetails] = useState<Order | null>(null);


	const changeTab = (tabId: number) => {
		setSelectedTab(tabId);
		setCurrentPage(1);
		setSearchValue("");
		setDebounceSearchValue("");
		setSelectedOrderType(tabId === 1 ? "Installment" : "Pay As You Go");
	};

	const tabs = [
		{ id: 1, title: "Loan Request", onClick: () => changeTab(1) },
		{ id: 2, title: "Pay-As-You-Go Request", onClick: () => changeTab(2) },
	];

	const {
		data: loanRequestData,
		isFetching,
		refetch: refetchLoanRequests,
		isError: isLoanRequestError,
		error: loanRequestError,
	} = useGetLoanRequestQuery(
		{
			Page: currentPage,
			pageSize: pageSize,
			country: selectedCountry?.isoCode ?? "",
			Type: selectedOrderType,
			...(debounceSearchValue && { search: debounceSearchValue }),
		},
		{
			skip: !selectedCountry?.isoCode,
		}
	);

	useEffect(() => {
		if (loanRequestData) {
			setData(loanRequestData.items);
			setTotalPages(loanRequestData.totalPages);
		}
	}, [loanRequestData]);

	useEffect(() => {
		if (isLoanRequestError && loanRequestError) {
			const errorMessage = "error" in loanRequestError ? `${loanRequestError.error}` : "Failed to fetch loan requests";
			toast.error(errorMessage);
		}
	}, [isLoanRequestError, loanRequestError]);

	useEffect(() => {
		setLoading(isFetching);
	}, [isFetching]);

	useEffect(() => {
		const timer = setTimeout(() => {
			setDebounceSearchValue(searchValue);
		}, 500);
		return () => clearTimeout(timer);
	}, [searchValue]);

	const refetchData = useCallback(() => {
		refetchLoanRequests();
	}, [refetchLoanRequests]);


	const handleEnter = (event: React.KeyboardEvent<HTMLInputElement>) => {
		if (event.key === "Enter") {
			event.preventDefault();
			setCurrentPage(1);
		}
	};

	if (orderDetails) {
		return <LoanRequestDetails orderType={selectedTab} orderDetails={orderDetails} setOrderDetails={setOrderDetails} refetchData={refetchData} />;
	}

	return (
		<section className={styles.loanRequestPage}>
			<div className={styles.pageTitle}>Requests</div>
			<div className={styles.contentWrapper}>
				<div className={styles.tabSelectorWrapper}>
					<div className={styles.tabContainer}>
						<TabSelector tabs={tabs} selectedTab={selectedTab} theme="yellow" />
					</div>
					<div className={styles.filtersWrapper}>
					<div className={styles.filters}>
						<GridSearchBar
							placeholder={"Search by name"}
							width="200px"
							onKeyDownHandler={handleEnter}
							onChangeHandler={value => setSearchValue(value)}
						/>
					</div>
				</div>
					<div className={styles.tableContainer}>
						<table className="standardTable">
							<thead>
								<tr>
									<th>DATE CREATED</th>
									<th>CUSTOMER NAME</th>
									<th>PHONE NUMBER</th>
									<th>EMAIL</th>
									<th>VIEW DETAILS</th>
								</tr>
							</thead>
							<tbody>
								{data?.map((item, index) => (
									<tr key={item.orderId || index}>
										<td>{item.createdAt ? moment(item.createdAt).format("h:mm A MMM DD, YYYY") : "-"}</td>
										<td>{item.user?.firstName + " " + item.user?.lastName}</td>
										<td>{item.user?.phoneNumber?.number}</td>
										<td>{item.user?.username}</td>
										<td>
											<button className={`actionButton`} onClick={() => setOrderDetails(item)}>
												REVIEW
											</button>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
					<div className={styles.paginationContainer}>
						<CommonPagination
							currentPage={currentPage}
							isLoading={loading}
							numberOfPages={totalPages}
							onPageChange={(page: number) => {
								setCurrentPage(page);
							}}
						/>
					</div>
				</div>
			</div>
		</section>
	);
});

export default LoanRequestPage;